import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Activity,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Zap,
  Clock,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Gauge,
  Target,
  BarChart3,
  Play,
  Pause,
  Radio,
  Wifi,
  WifiOff,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { format, subDays } from 'date-fns';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RTBAnalyticsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [realtimeStats, setRealtimeStats] = useState(null);
  const [floorOptimization, setFloorOptimization] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
  });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [simulatorRunning, setSimulatorRunning] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [liveEvents, setLiveEvents] = useState([]);
  const [liveStats, setLiveStats] = useState({
    bids: 0,
    wins: 0,
    revenue: 0
  });
  
  const intervalRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/ws/rtb`);
    
    ws.onopen = () => {
      setWsConnected(true);
      toast.success('Connected to real-time feed');
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'rtb_event') {
        const rtbEvent = data.data;
        setLiveEvents(prev => [rtbEvent, ...prev].slice(0, 50)); // Keep last 50 events
        
        // Update live stats
        setLiveStats(prev => ({
          bids: prev.bids + (rtbEvent.event_type === 'bid_response' ? 1 : 0),
          wins: prev.wins + (rtbEvent.event_type === 'win' ? 1 : 0),
          revenue: prev.revenue + (rtbEvent.winning_price || 0)
        }));
      }
    };
    
    ws.onclose = () => {
      setWsConnected(false);
      // Auto-reconnect after 3 seconds
      if (autoRefresh) {
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
      }
    };
    
    ws.onerror = () => {
      setWsConnected(false);
    };
    
    wsRef.current = ws;
  }, [autoRefresh]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  // Connect WebSocket when autoRefresh is enabled
  useEffect(() => {
    if (autoRefresh) {
      connectWebSocket();
    } else {
      if (wsRef.current) {
        wsRef.current.close();
      }
    }
  }, [autoRefresh, connectWebSocket]);

  useEffect(() => {
    loadData();
    loadRealtime();
    loadSimulatorStatus();
    
    // Set up auto-refresh for realtime data
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        loadRealtime();
        loadRealtimeStats();
      }, 10000); // Every 10 seconds
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [dateRange, autoRefresh]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [analyticsRes, floorRes] = await Promise.all([
        api.get('/rtb/analytics', {
          params: { start_date: dateRange.start, end_date: dateRange.end }
        }),
        api.get('/rtb/bid-floor-optimization'),
      ]);
      setAnalytics(analyticsRes.data);
      setFloorOptimization(floorRes.data);
    } catch (error) {
      toast.error('Failed to load RTB analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadRealtime = async () => {
    try {
      const response = await api.get('/rtb/realtime');
      setRealtime(response.data);
    } catch (error) {
      console.error('Failed to load realtime data');
    }
  };

  const loadRealtimeStats = async () => {
    try {
      const response = await api.get('/realtime/stats');
      setRealtimeStats(response.data);
    } catch (error) {
      console.error('Failed to load realtime stats');
    }
  };

  const loadSimulatorStatus = async () => {
    try {
      const response = await api.get('/realtime/simulator/status');
      setSimulatorRunning(response.data.running);
      setRealtimeStats(response.data);
    } catch (error) {
      console.error('Failed to load simulator status');
    }
  };

  const toggleSimulator = async () => {
    try {
      if (simulatorRunning) {
        await api.post('/realtime/simulator/stop');
        toast.success('Real-time simulator stopped');
        setSimulatorRunning(false);
      } else {
        await api.post('/realtime/simulator/start');
        toast.success('Real-time simulator started');
        setSimulatorRunning(true);
        // Reset live stats when starting
        setLiveStats({ bids: 0, wins: 0, revenue: 0 });
        setLiveEvents([]);
      }
    } catch (error) {
      toast.error('Failed to toggle simulator');
    }
  };

  const getChangeIndicator = (value, isPositive = true) => {
    const color = isPositive ? 'text-emerald-400' : 'text-red-400';
    const Icon = isPositive ? ArrowUpRight : ArrowDownRight;
    return (
      <span className={`flex items-center gap-1 text-sm ${color}`}>
        <Icon className="w-4 h-4" />
        {value}
      </span>
    );
  };

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rtb-analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            RTB Analytics Dashboard
          </h1>
          <p className="text-zinc-400 mt-1">
            Real-time bidding performance and optimization insights
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* WebSocket Status */}
          <Badge 
            variant="outline" 
            className={wsConnected ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}
          >
            {wsConnected ? <Wifi className="w-3 h-3 mr-1" /> : <WifiOff className="w-3 h-3 mr-1" />}
            {wsConnected ? 'Connected' : 'Disconnected'}
          </Badge>
          
          {/* Simulator Toggle */}
          <Button
            variant={simulatorRunning ? 'default' : 'outline'}
            size="sm"
            onClick={toggleSimulator}
            className={simulatorRunning ? 'bg-purple-600 hover:bg-purple-700' : 'border-zinc-700'}
            data-testid="simulator-toggle"
          >
            {simulatorRunning ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
            {simulatorRunning ? 'Stop Simulator' : 'Start Simulator'}
          </Button>
          
          <Button
            variant={autoRefresh ? 'default' : 'outline'}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? 'bg-emerald-600' : 'border-zinc-700'}
          >
            <Activity className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-pulse' : ''}`} />
            {autoRefresh ? 'Live' : 'Paused'}
          </Button>
          <Button variant="outline" onClick={loadData} className="border-zinc-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Live Session Stats (from WebSocket) */}
      {simulatorRunning && (
        <div className="glass-card border-purple-500/30 bg-purple-500/5 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Radio className="w-4 h-4 text-purple-400 animate-pulse" />
              <span className="text-purple-400 text-sm font-medium">Live Session Stats</span>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-white font-bold text-lg">{liveStats.bids.toLocaleString()}</p>
                <p className="text-zinc-500 text-xs">Session Bids</p>
              </div>
              <div className="text-center">
                <p className="text-white font-bold text-lg">{liveStats.wins.toLocaleString()}</p>
                <p className="text-zinc-500 text-xs">Session Wins</p>
              </div>
              <div className="text-center">
                <p className="text-emerald-400 font-bold text-lg">${liveStats.revenue.toFixed(2)}</p>
                <p className="text-zinc-500 text-xs">Session Revenue</p>
              </div>
              <div className="text-center">
                <p className="text-blue-400 font-bold text-lg">
                  {liveStats.bids > 0 ? ((liveStats.wins / liveStats.bids) * 100).toFixed(1) : 0}%
                </p>
                <p className="text-zinc-500 text-xs">Win Rate</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Real-time Stats Bar */}
      {realtime && (
        <div className="glass-card border-zinc-800 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-zinc-400 text-sm">Live (Last 5 min)</span>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-white font-bold">{realtime.last_5_minutes?.bid_requests?.toLocaleString()}</p>
                <p className="text-zinc-500 text-xs">Bid Requests</p>
              </div>
              <div className="text-center">
                <p className="text-white font-bold">{realtime.last_5_minutes?.wins?.toLocaleString()}</p>
                <p className="text-zinc-500 text-xs">Wins</p>
              </div>
              <div className="text-center">
                <p className="text-emerald-400 font-bold">${realtime.last_5_minutes?.revenue?.toFixed(2)}</p>
                <p className="text-zinc-500 text-xs">Revenue</p>
              </div>
              <div className="text-center">
                <p className="text-blue-400 font-bold">{realtime.current_qps}</p>
                <p className="text-zinc-500 text-xs">QPS</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Date Range Selector */}
      <Card className="glass-card border-zinc-800">
        <CardContent className="p-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label className="text-zinc-400 text-xs">Start Date</Label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="bg-zinc-900 border-zinc-800 text-white"
              />
            </div>
            <div className="flex-1">
              <Label className="text-zinc-400 text-xs">End Date</Label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="bg-zinc-900 border-zinc-800 text-white"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4 text-blue-400" />
              <span className="text-zinc-400 text-xs">Bid Requests</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {(analytics?.summary?.bid_requests || 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-4 h-4 text-purple-400" />
              <span className="text-zinc-400 text-xs">Wins</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {(analytics?.summary?.wins || 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4 text-emerald-400" />
              <span className="text-zinc-400 text-xs">Revenue</span>
            </div>
            <p className="text-2xl font-bold text-emerald-400">
              ${(analytics?.summary?.total_revenue || 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Gauge className="w-4 h-4 text-amber-400" />
              <span className="text-zinc-400 text-xs">Fill Rate</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {analytics?.summary?.fill_rate || 0}%
            </p>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-blue-400" />
              <span className="text-zinc-400 text-xs">Win Rate</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {analytics?.summary?.win_rate || 0}%
            </p>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-4 h-4 text-purple-400" />
              <span className="text-zinc-400 text-xs">eCPM</span>
            </div>
            <p className="text-2xl font-bold text-white">
              ${analytics?.summary?.ecpm || 0}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Latency Metrics */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-400" />
            Latency Metrics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-zinc-800/50">
              <p className="text-zinc-400 text-sm">Avg Response</p>
              <p className="text-2xl font-bold text-white">{analytics?.latency?.avg_response_time_ms}ms</p>
            </div>
            <div className="p-4 rounded-lg bg-zinc-800/50">
              <p className="text-zinc-400 text-sm">P95 Response</p>
              <p className="text-2xl font-bold text-white">{analytics?.latency?.p95_response_time_ms}ms</p>
            </div>
            <div className="p-4 rounded-lg bg-zinc-800/50">
              <p className="text-zinc-400 text-sm">P99 Response</p>
              <p className="text-2xl font-bold text-white">{analytics?.latency?.p99_response_time_ms}ms</p>
            </div>
            <div className="p-4 rounded-lg bg-zinc-800/50">
              <p className="text-zinc-400 text-sm">Timeout Rate</p>
              <p className="text-2xl font-bold text-amber-400">{analytics?.latency?.timeout_rate}%</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Trend */}
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white">Daily Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analytics?.daily_trend || []}>
                  <defs>
                    <linearGradient id="impressionsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis
                    dataKey="date"
                    stroke="#71717a"
                    fontSize={10}
                    tickFormatter={(value) => format(new Date(value), 'MMM d')}
                  />
                  <YAxis stroke="#71717a" fontSize={10} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#18181b',
                      border: '1px solid #27272a',
                      borderRadius: '8px',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="impressions"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fill="url(#impressionsGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Hourly Distribution */}
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white">Hourly Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics?.hourly_distribution || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis
                    dataKey="hour"
                    stroke="#71717a"
                    fontSize={10}
                    tickFormatter={(h) => `${h}:00`}
                  />
                  <YAxis stroke="#71717a" fontSize={10} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#18181b',
                      border: '1px solid #27272a',
                      borderRadius: '8px',
                    }}
                  />
                  <Bar dataKey="impressions" fill="#8b5cf6" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* DSP Breakdown */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white">DSP Performance</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-500">DSP</TableHead>
                <TableHead className="text-zinc-500 text-right">Bid Requests</TableHead>
                <TableHead className="text-zinc-500 text-right">Wins</TableHead>
                <TableHead className="text-zinc-500 text-right">Win Rate</TableHead>
                <TableHead className="text-zinc-500 text-right">Avg Bid</TableHead>
                <TableHead className="text-zinc-500 text-right">Revenue</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {analytics?.dsp_breakdown?.map((dsp, index) => (
                <TableRow key={index} className="border-zinc-800">
                  <TableCell className="text-white font-medium">{dsp.dsp_name}</TableCell>
                  <TableCell className="text-zinc-400 text-right">
                    {dsp.bid_requests?.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-zinc-400 text-right">{dsp.wins?.toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline" className={
                      dsp.win_rate > 30
                        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                        : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                    }>
                      {dsp.win_rate}%
                    </Badge>
                  </TableCell>
                  <TableCell className="text-zinc-400 text-right">${dsp.avg_bid}</TableCell>
                  <TableCell className="text-emerald-400 text-right font-medium">
                    ${dsp.revenue?.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Floor Price Optimization */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white">Floor Price Optimization</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <p className="text-emerald-400 text-sm">Increase Floor</p>
              <p className="text-2xl font-bold text-emerald-400">
                {floorOptimization?.summary?.screens_to_increase || 0}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-red-400 text-sm">Decrease Floor</p>
              <p className="text-2xl font-bold text-red-400">
                {floorOptimization?.summary?.screens_to_decrease || 0}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-zinc-500/10 border border-zinc-500/20">
              <p className="text-zinc-400 text-sm">Optimal</p>
              <p className="text-2xl font-bold text-zinc-400">
                {floorOptimization?.summary?.screens_optimal || 0}
              </p>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-500">Screen</TableHead>
                <TableHead className="text-zinc-500 text-right">Current Floor</TableHead>
                <TableHead className="text-zinc-500 text-right">Win Rate</TableHead>
                <TableHead className="text-zinc-500">Recommendation</TableHead>
                <TableHead className="text-zinc-500 text-right">Suggested</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {floorOptimization?.recommendations?.map((rec, index) => (
                <TableRow key={index} className="border-zinc-800">
                  <TableCell className="text-white font-medium">{rec.screen_name}</TableCell>
                  <TableCell className="text-zinc-400 text-right">${rec.current_floor}</TableCell>
                  <TableCell className="text-zinc-400 text-right">{rec.win_rate}%</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={
                      rec.recommendation === 'increase'
                        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                        : rec.recommendation === 'decrease'
                        ? 'bg-red-500/10 text-red-400 border-red-500/20'
                        : 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
                    }>
                      {rec.recommendation === 'increase' && <TrendingUp className="w-3 h-3 mr-1" />}
                      {rec.recommendation === 'decrease' && <TrendingDown className="w-3 h-3 mr-1" />}
                      {rec.recommendation}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={
                      rec.recommendation === 'increase' ? 'text-emerald-400' :
                      rec.recommendation === 'decrease' ? 'text-red-400' : 'text-zinc-400'
                    }>
                      ${rec.suggested_floor} ({rec.potential_revenue_change})
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Live Event Feed */}
      {liveEvents.length > 0 && (
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Radio className="w-5 h-5 text-purple-400 animate-pulse" />
              Live Event Feed
              <Badge variant="outline" className="ml-2 bg-purple-500/10 text-purple-400 border-purple-500/20">
                {liveEvents.length} events
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-80 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent sticky top-0 bg-zinc-900">
                    <TableHead className="text-zinc-500 w-24">Time</TableHead>
                    <TableHead className="text-zinc-500">Type</TableHead>
                    <TableHead className="text-zinc-500">DSP</TableHead>
                    <TableHead className="text-zinc-500">Screen</TableHead>
                    <TableHead className="text-zinc-500 text-right">Bid</TableHead>
                    <TableHead className="text-zinc-500 text-right">Win Price</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {liveEvents.map((event, index) => (
                    <TableRow 
                      key={event.id || index} 
                      className={`border-zinc-800 transition-colors ${index === 0 ? 'bg-purple-500/5' : ''}`}
                    >
                      <TableCell className="text-zinc-500 text-xs font-mono">
                        {new Date(event.timestamp).toLocaleTimeString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          event.event_type === 'win'
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                            : 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                        }>
                          {event.event_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-white text-sm">{event.dsp_name}</TableCell>
                      <TableCell className="text-zinc-400 text-sm">{event.screen_name}</TableCell>
                      <TableCell className="text-zinc-400 text-right">${event.bid_price?.toFixed(2)}</TableCell>
                      <TableCell className="text-right">
                        {event.winning_price ? (
                          <span className="text-emerald-400 font-medium">${event.winning_price.toFixed(2)}</span>
                        ) : (
                          <span className="text-zinc-600">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
